/**
 * Auth Callback — handles redirects from Google OAuth and magic link.
 * Extracts tokens from URL params and stores them in auth state.
 *
 * Note: Ghost login (impersonation) is handled by GhostLogin.tsx, not here.
 */
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';

export default function AuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setTokens, checkAuth } = useAuthStore();

  useEffect(() => {
    const accessToken = searchParams.get('accessToken');
    const refreshToken = searchParams.get('refreshToken');
    const error = searchParams.get('error');
    // Backend flags a first-time Google/magic-link signup — send them to the
    // welcome wizard instead of an empty dashboard.
    const isNew = searchParams.get('isNew') === '1';

    if (error) {
      navigate(`/login?error=${error}`, { replace: true });
      return;
    }

    if (accessToken && refreshToken) {
      setTokens(accessToken, refreshToken);
      // Fetch user data (role, name, etc.) before navigating
      checkAuth().then(() => {
        // Check if there's a saved redirect (e.g., from invite page → login → Google OAuth)
        const savedRedirect = sessionStorage.getItem('auth_redirect');
        sessionStorage.removeItem('auth_redirect');
        navigate(savedRedirect || (isNew ? '/welcome' : '/dashboard'), { replace: true });
      });
    } else {
      navigate('/login?error=missing_tokens', { replace: true });
    }
  }, [searchParams, navigate, setTokens, checkAuth]);

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="text-center">
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
        <p className="mt-4 text-gray-500">Signing you in...</p>
      </div>
    </div>
  );
}
